import React, { useState } from 'react';
import {
  generateDealInterpretation,
  generateIncomeInterpretation,
  generateObjectInterpretation,
  generateLocationInterpretation,
} from './utils/interpretations';

/* Typ-5 Aufklapp-Trigger: primary-400 Text + Chevron */
function WharumTrigger({ open, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-1 shrink-0 rounded transition-all"
      style={{
        color: open ? '#0E7A6B' : '#1DB3B3',
        padding: '4px 6px',
        background: 'transparent',
        border: 'none',
        fontSize: '13px',
        fontWeight: 500,
        lineHeight: '20px',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#E8F7F7'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {open ? 'Weniger' : 'Warum?'}
      <svg
        width="12" height="12" viewBox="0 0 12 12" fill="none"
        style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}
      >
        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </button>
  );
}

function FactorRow({ label, score, max, color, interpretation }) {
  const [open, setOpen] = useState(false);
  const pct = max > 0 ? (score / max) * 100 : 0;

  return (
    <div>
      <div className="flex items-center gap-3" style={{ height: 40 }}>
        {/* Label */}
        <span className="text-[14px] font-medium shrink-0" style={{ width: 80, color: '#5D5D6D' }}>
          {label}
        </span>
        {/* Bar */}
        <div className="flex-1 rounded-full overflow-hidden" style={{ height: 12, background: '#E0E0DF' }}>
          <div
            className="h-full rounded-full"
            style={{ width: `${pct}%`, background: color, transition: 'width 0.7s ease' }}
          />
        </div>
        {/* Score */}
        <span
          className="text-[14px] font-semibold shrink-0 text-right"
          style={{ width: 50, color: '#1F1F1F', fontVariantNumeric: 'tabular-nums' }}
        >
          {score}/{max}
        </span>
        {/* Warum? Trigger */}
        <WharumTrigger open={open} onToggle={() => setOpen(!open)} />
      </div>

      {/* Aufgeklappte Interpretation */}
      {open && (
        <div
          className="text-[13px] leading-[1.6] rounded-sm"
          style={{
            marginLeft: 92,
            marginRight: 70,
            marginTop: 6,
            marginBottom: 8,
            padding: '8px 12px',
            background: '#E8F7F7',
            borderLeft: '2px solid #1DB3B3',
            color: '#5D5D6D',
            animation: 'slideDown 200ms ease-out',
          }}
        >
          {interpretation || 'Keine Interpretation verfügbar.'}
        </div>
      )}
    </div>
  );
}

export default function ScoreBreakdown({ baseline, subscores, signals, market }) {
  if (!subscores) return null;

  const enrichedDetails = {
    deal: {
      dealDelta: baseline?.marketOverMarket != null ? baseline.marketOverMarket / 100 : null,
      faktor: baseline?.faktor ?? null,
    },
    income: {
      bruttoRendite: baseline?.bruttoRendite ?? null,
      cashflow: baseline?.cf?.cashflow ?? null,
    },
    object: {
      baujahr: baseline?.baujahr ?? null,
    },
    location: {},
    financing: { zinssatz: baseline?.zinssatz ?? 0.035 },
  };

  const interpretations = {
    Deal:   generateDealInterpretation(enrichedDetails, market),
    Ertrag: generateIncomeInterpretation(enrichedDetails, baseline),
    Objekt: generateObjectInterpretation(enrichedDetails, signals),
    Lage:   generateLocationInterpretation(enrichedDetails, market),
  };

  const FACTOR_COLORS = {
    Deal:   '#1DB3B3',
    Ertrag: '#0E7A6B',
    Objekt: '#3FC0C0',
    Lage:   '#0B5C52',
  };

  return (
    <div className="bg-white rounded-2xl p-5" style={{ border: '1px solid #E0E0DF' }}>
      <h3 className="text-[20px] font-semibold text-[#1F1F1F] mb-4">Score-Aufschlüsselung</h3>

      <div className="space-y-0">
        {Object.values(subscores).map(s => (
          <FactorRow
            key={s.label}
            label={s.label}
            score={s.score}
            max={s.max}
            color={FACTOR_COLORS[s.label] || s.color}
            interpretation={interpretations[s.label]}
          />
        ))}
      </div>

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; max-height: 0; overflow: hidden; }
          to { opacity: 1; max-height: 200px; }
        }
      `}</style>
    </div>
  );
}
